import { useMemo, useState, useTransition } from 'react';
import { buildUrl, classNames } from '@scripts/lib/formatters';

/**
 * Mount target:
 *   <div data-island="product-filters" data-props="{...}"></div>
 *
 * Props contract:
 * - title: string
 * - resultsLabel: string
 * - resetLabel: string
 * - applyLabel: string
 * - actionUrl: string
 * - autoSubmit: boolean
 * - totalResults: number
 * - currentSort: string
 * - sortOptions: Array<{ value: string, label: string }>
 * - groups: Array<{ key: string, label: string, type?: 'checkbox'|'radio', options: Array<{ value: string, label: string, checked?: boolean, count?: number }> }>
 *
 * Trigger conditions:
 * - Render this island on shop archives when asynchronous UI feedback,
 *   querystring-driven filters, or drawer/mobile filter presentation is needed.
 */
export function ProductFiltersIsland({
  title = 'Filter Products',
  resultsLabel = 'Results',
  resetLabel = 'Reset',
  applyLabel = 'Apply Filters',
  sortLabel = 'Sort',
  defaultSortLabel = 'Default',
  actionUrl = window.location.href,
  autoSubmit = false,
  totalResults = 0,
  currentSort = '',
  sortOptions = [],
  groups = [],
}) {
  const [selectedFilters, setSelectedFilters] = useState(() =>
    groups.reduce((state, group) => {
      const checkedValues = (group.options || [])
        .filter((option) => Boolean(option.checked))
        .map((option) => String(option.value));

      return {
        ...state,
        [group.key]: group.type === 'radio' ? checkedValues[0] || '' : checkedValues,
      };
    }, {})
  );
  const [sortValue, setSortValue] = useState(currentSort);
  const [isPending, startTransition] = useTransition();

  const appliedCount = useMemo(
    () =>
      Object.values(selectedFilters).reduce((count, value) => {
        if (Array.isArray(value)) {
          return count + value.length;
        }

        return value ? count + 1 : count;
      }, 0),
    [selectedFilters]
  );

  function submitFilters(nextFilters, nextSortValue) {
    const nextUrl = buildUrl(actionUrl, {
      ...nextFilters,
      orderby: nextSortValue,
    });

    window.location.assign(nextUrl);
  }

  function handleOptionChange(groupKey, groupType, optionValue) {
    startTransition(() => {
      setSelectedFilters((currentFilters) => {
        const currentValue = currentFilters[groupKey];
        const nextValue =
          groupType === 'radio'
            ? optionValue
            : Array.isArray(currentValue)
              ? currentValue.includes(optionValue)
                ? currentValue.filter((value) => value !== optionValue)
                : [...currentValue, optionValue]
              : [optionValue];

        const nextFilters = {
          ...currentFilters,
          [groupKey]: nextValue,
        };

        if (autoSubmit) {
          submitFilters(nextFilters, sortValue);
        }

        return nextFilters;
      });
    });
  }

  function handleSortChange(event) {
    const nextSortValue = event.target.value;
    setSortValue(nextSortValue);

    if (autoSubmit) {
      submitFilters(selectedFilters, nextSortValue);
    }
  }

  function handleReset() {
    const resetFilters = groups.reduce((state, group) => {
      state[group.key] = group.type === 'radio' ? '' : [];
      return state;
    }, {});

    setSelectedFilters(resetFilters);
    setSortValue('');
    submitFilters(resetFilters, '');
  }

  return (
    <section className="island-panel space-y-5" aria-busy={isPending}>
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-500">
            {resultsLabel}: {totalResults}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
            {appliedCount}
          </span>
          <button
            type="button"
            className="text-sm font-medium text-slate-500 transition hover:text-brand-700"
            onClick={handleReset}
          >
            {resetLabel}
          </button>
        </div>
      </header>

      {sortOptions.length > 0 ? (
        <label className="block space-y-2">
          <span className="text-sm font-medium text-slate-700">{sortLabel}</span>
          <select
            className="island-input"
            value={sortValue}
            onChange={handleSortChange}
          >
            <option value="">{defaultSortLabel}</option>
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <div className="grid gap-5 md:grid-cols-2">
        {groups.map((group) => {
          const value = selectedFilters[group.key];

          return (
            <section key={group.key} className="space-y-3 rounded-2xl border border-slate-200 p-4">
              <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-600">{group.label}</h4>
              <div className="space-y-2">
                {(group.options || []).map((option) => {
                  const isChecked = Array.isArray(value)
                    ? value.includes(option.value)
                    : value === option.value;

                  return (
                    <label
                      key={option.value}
                      className={classNames(
                        'flex cursor-pointer items-center justify-between gap-3 rounded-2xl border px-3 py-3 text-sm transition',
                        isChecked
                          ? 'border-brand-400 bg-brand-50 text-brand-900'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                      )}
                    >
                      <span className="flex items-center gap-3">
                        <input
                          type={group.type === 'radio' ? 'radio' : 'checkbox'}
                          name={group.key}
                          value={option.value}
                          checked={isChecked}
                          onChange={() => handleOptionChange(group.key, group.type || 'checkbox', option.value)}
                        />
                        <span>{option.label}</span>
                      </span>
                      {option.count !== undefined ? <span className="text-xs text-slate-500">{option.count}</span> : null}
                    </label>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {!autoSubmit ? (
        <button
          type="button"
          className="button-primary min-h-11"
          onClick={() => submitFilters(selectedFilters, sortValue)}
        >
          {applyLabel}
        </button>
      ) : null}
    </section>
  );
}
